/**
 * Billing view logic (FLOW-700).
 *
 * Everything the `/settings/billing` surface decides about a
 * `TenantBillingStatus` payload, or about an error thrown by the checkout and
 * portal routes, lives here so the page itself stays assembly.
 */

import { ApiError } from "../api/client";
import type { BadgeVariant } from "../components/ui";
import type {
  BillingPlan,
  QuotaCap,
  SubscriptionStatus,
  TenantBillingStatus,
  TokenPack,
} from "../api/types";

/**
 * The statuses Stripe reports for a subscription that still exists — the
 * tenant has bought something, so the surface shows the subscription card
 * rather than the plan picker. `past_due`/`unpaid` are included on purpose:
 * the subscription is live and needs paying, not re-buying.
 */
const LIVE_STATUSES = new Set<SubscriptionStatus>([
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "paused",
]);

export function isLiveSubscription(
  status: SubscriptionStatus | null | undefined,
): boolean {
  return status !== null && status !== undefined && LIVE_STATUSES.has(status);
}

export type TrialState = "none" | "active" | "ended";

/** Display-only: the backend does not enforce trial expiry, so "ended" says when, not what stopped. */
export function trialState(
  trialEndsAt: string | null,
  now: Date = new Date(),
): TrialState {
  if (!trialEndsAt) return "none";
  const ends = new Date(trialEndsAt).getTime();
  if (Number.isNaN(ends)) return "none";
  return ends > now.getTime() ? "active" : "ended";
}

export type CurrentPlan = "trial" | BillingPlan | null;

export function currentPlan(
  billing: TenantBillingStatus,
  now: Date = new Date(),
): CurrentPlan {
  if (isLiveSubscription(billing.subscription_status)) {
    // `plan` is absent, not null, on a payload whose Price is off-catalogue.
    return billing.plan ?? null;
  }
  return trialState(billing.trial_ends_at, now) === "active" ? "trial" : null;
}

/**
 * `paused` is live but not healthy, so it takes the neutral variant rather
 * than the green one — green would assert everything is fine (FLOW-700 R7).
 */
const BADGE_VARIANTS: Partial<Record<SubscriptionStatus, BadgeVariant>> = {
  active: "done",
  trialing: "done",
  past_due: "inReview",
  unpaid: "inReview",
  canceled: "cancelled",
};

export function subscriptionBadgeVariant(
  status: SubscriptionStatus | null,
): BadgeVariant {
  return (status && BADGE_VARIANTS[status]) ?? "default";
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  incomplete: "Incomplete",
  incomplete_expired: "Incomplete (expired)",
  trialing: "Trial",
  active: "Active",
  past_due: "Past due",
  canceled: "Cancelled",
  unpaid: "Unpaid",
  paused: "Paused",
};

export function subscriptionStatusLabel(
  status: SubscriptionStatus | null,
): string {
  return status === null ? "No subscription" : STATUS_LABELS[status];
}

/** A medium local date, or null when the API sent nothing usable. */
export function formatPeriodDate(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * What the page should do about a failed checkout or portal call.
 *
 * `unavailable` — the deployment has no Stripe configured, so the whole
 * surface swaps to that explanation. `stale` — the tenant's subscription
 * changed between the status read and the click, so refetch and let the
 * fresh payload decide what to render. `message` — show the copy inline
 * beside the button that failed.
 */
export type BillingErrorOutcome =
  | { kind: "unavailable" }
  | { kind: "stale" }
  | { kind: "message"; message: string };

const CODE_MESSAGES: Record<string, string> = {
  // Word-for-word the backend's own `PLAN_SEAT_LIMIT` copy (FLOW-888 D4), so the
  // two cannot drift again: the seat is the tenant's one admin, and telling an
  // admin to remove *members* — as this said until D4 made guests free — names an
  // action that clears the refusal not at all.
  PLAN_SEAT_LIMIT:
    "The Personal plan includes one seat; demote the extra admins or choose Pro",
  // Word-for-word the backend's own `PLAN_GUEST_LIMIT` copy too (FLOW-888 R11).
  // The Personal plan's other entitlement: the seat rule is about admins, this
  // one is about a project holding more free guests than the plan includes, and
  // the action that clears it is removing a collaborator.
  PLAN_GUEST_LIMIT:
    "The Personal plan includes 2 free guest collaborators per project; remove the extra collaborators or choose Pro",
  NO_BILLING_CUSTOMER: "No billing account yet",
  SUBSCRIPTION_REQUIRED: "Token packs need an active subscription",
  NO_MONTHLY_CAP: "Your organization has no monthly AI budget to top up",
};

export function classifyBillingError(err: unknown): BillingErrorOutcome {
  if (err instanceof ApiError) {
    if (err.status === 503) return { kind: "unavailable" };
    const code = (err.data as { code?: string } | null)?.code;
    if (code === "SUBSCRIPTION_EXISTS" || code === "CHECKOUT_PENDING") {
      return { kind: "stale" };
    }
    if (code && CODE_MESSAGES[code]) {
      return { kind: "message", message: CODE_MESSAGES[code] };
    }
    if (err.status === 429) {
      return {
        kind: "message",
        message: "Too many attempts, try again in a minute",
      };
    }
    return { kind: "message", message: err.message };
  }
  return {
    kind: "message",
    message: err instanceof Error ? err.message : "Something went wrong.",
  };
}

/**
 * Which verb the `current_period_end` date belongs to. A cancelled
 * subscription still has a paid-through date, and calling that a renewal
 * would promise a charge that will not happen.
 */
export function periodDateLabel(status: SubscriptionStatus | null): string {
  return status === "canceled" ? "Ends on" : "Renews on";
}

/**
 * The add-on packs on offer (FLOW-851). Sizes are fixed by the item; the
 * prices are static copy duplicating the Stripe Prices, exactly as
 * `PLAN_COPY` does — changing one means changing both.
 */
export const TOKEN_PACKS: {
  pack: TokenPack;
  label: string;
  price: string;
}[] = [
  { pack: "5m", label: "+5M tokens", price: "$10" },
  { pack: "15m", label: "+15M tokens", price: "$25" },
];

/** A token count as millions — the only magnitude packs come in. */
export function formatTokens(tokens: number): string {
  return tokens / 1_000_000 + "M";
}

/** The pack the paywall offers — the $10 one the item names. */
export const PAYWALL_PACK: TokenPack = "5m";

/**
 * The add-on pack to offer on a refusal, or `null` when no pack would clear it
 * (FLOW-928 D1). Pack credit lands on the UTC month alone, so a daily-burst
 * refusal and a project-ceiling refusal (which carries no `cap` at all) are
 * both unaffected by a purchase; and a pack needs a live subscription to top up.
 */
export function paywallPackOffer(
  billing: TenantBillingStatus | undefined,
  cap: QuotaCap | null,
): { pack: TokenPack; label: string } | null {
  if (cap !== "monthly") return null;
  if (!isLiveSubscription(billing?.subscription_status)) return null;
  const option = TOKEN_PACKS.find((o) => o.pack === PAYWALL_PACK);
  return option
    ? { pack: option.pack, label: `Buy ${option.label} · ${option.price}` }
    : null;
}

/** The paywall's upgrade-trip label, or `null` for a tenant with nothing to buy (D5). */
export function upgradeCta(
  billing: TenantBillingStatus | undefined,
): string | null {
  if (!billing) return "View plans";
  if (!isLiveSubscription(billing.subscription_status)) return "Choose a plan";
  if (billing.plan === "pro") return null;
  if (billing.plan === "personal") return "Upgrade to Pro";
  return "View plans";
}

/**
 * The month's pack total as it stood when the admin left for Stripe, carried
 * across the round trip (FLOW-851 R7). The webhook routinely lands before the
 * SPA has booted and read billing, so a baseline taken from the first payload
 * back already includes the credit and no growth is ever seen. Both accessors
 * are guarded: `sessionStorage` throws outright in some private modes, and the
 * poll's own first-payload rule remains the fallback.
 */
const PACK_BASELINE_KEY = "flow851-pack-baseline";

export const storePackBaseline = (total: number) => {
  try {
    sessionStorage.setItem(PACK_BASELINE_KEY, String(total));
  } catch {
    // Nothing to do: the poll falls back to the first payload it reads.
  }
};

/** Reads the baseline and consumes it, so a later visit cannot reuse it. */
export const takePackBaseline = (): number | undefined => {
  try {
    const stored = sessionStorage.getItem(PACK_BASELINE_KEY);
    sessionStorage.removeItem(PACK_BASELINE_KEY);
    if (stored === null) return undefined;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};
